import { create } from 'zustand'
import { supabase } from '@/shared/utils/supabase'
import { NotificationsApi, type Notification } from '@/shared/api/notifications'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { useAuthStore } from './authStore'

const DEFAULT_LIMIT = 50

const getAuthToken = async (): Promise<string | null> => {
  return localStorage.getItem('access_token')
}

const notificationsApi = new NotificationsApi(getAuthToken)

type NotificationStatus = 'unread' | 'read' | 'archived'
type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent'

interface FetchParams {
  status?: NotificationStatus
  type?: string
  priority?: NotificationPriority
  limit?: number
  tripId?: string
}

interface FetchOptions extends FetchParams {
  silent?: boolean
}

interface NotificationStoreState {
  notifications: Notification[]
  unreadCount: number
  hasMore: boolean
  loading: boolean
  error: string | null
  isInitialized: boolean
  channel: RealtimeChannel | null
  fetchParams: FetchParams
  fetchNotifications: (options?: FetchOptions) => Promise<void>
  refresh: () => Promise<void>
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotification: (notificationId: string) => Promise<void>
  deleteAllNotifications: () => Promise<void>
  startRealtime: () => Promise<void>
  stopRealtime: () => void
  reset: () => void
}

let inFlightFetch: Promise<void> | null = null
let previousUserId: string | null = null

export const useNotificationStore = create<NotificationStoreState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  hasMore: false,
  loading: false,
  error: null,
  isInitialized: false,
  channel: null,
  fetchParams: { limit: DEFAULT_LIMIT },

  fetchNotifications: async (options?: FetchOptions) => {
    const user = useAuthStore.getState().user

    if (!user) {
      get().reset()
      return
    }

    const { silent, ...params } = options ?? {}
    const nextParams: FetchParams = {
      ...get().fetchParams,
      ...params
    }

    if (inFlightFetch) {
      return inFlightFetch
    }

    set({
      fetchParams: nextParams,
      loading: silent ? get().loading : !get().isInitialized,
      error: null
    })

    inFlightFetch = (async () => {
      try {
        const response = await notificationsApi.getNotifications({
          status: nextParams.status,
          type: nextParams.type,
          priority: nextParams.priority,
          limit: nextParams.limit ?? DEFAULT_LIMIT,
          sortBy: 'created_at',
          sortOrder: 'desc',
          tripId: nextParams.tripId
        })

        set({
          notifications: response.notifications,
          unreadCount: response.unreadCount,
          hasMore: response.hasMore,
          loading: false,
          error: null,
          isInitialized: true
        })

        await get().startRealtime()
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch notifications'
        set({
          error: message,
          loading: false,
          isInitialized: true
        })
        throw err
      } finally {
        inFlightFetch = null
      }
    })()

    return inFlightFetch
  },

  refresh: async () => {
    await get().fetchNotifications({ silent: true })
  },

  markAsRead: async (notificationId: string) => {
    await notificationsApi.markAsRead(notificationId)

    set(state => {
      const notifications = state.notifications.map<Notification>(notification => {
        if (notification.id !== notificationId) {
          return notification
        }
        return {
          ...notification,
          status: 'read',
          readAt: new Date().toISOString()
        }
      })

      const wasUnread = state.notifications.some(
        notificationItem => notificationItem.id === notificationId && notificationItem.status === 'unread'
      )

      return {
        notifications,
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount
      }
    })
  },

  markAllAsRead: async () => {
    await notificationsApi.markAllAsRead()

    set(state => ({
      notifications: state.notifications.map<Notification>(notification => ({
        ...notification,
        status: 'read',
        readAt: notification.readAt ?? new Date().toISOString()
      })),
      unreadCount: 0
    }))
  },

  deleteNotification: async (notificationId: string) => {
    await notificationsApi.deleteNotification(notificationId)

    set(state => {
      const notification = state.notifications.find(item => item.id === notificationId)
      return {
        notifications: state.notifications.filter(item => item.id !== notificationId),
        unreadCount:
          notification?.status === 'unread'
            ? Math.max(0, state.unreadCount - 1)
            : state.unreadCount
      }
    })
  },

  deleteAllNotifications: async () => {
    await notificationsApi.deleteAllNotifications()

    set({
      notifications: [],
      unreadCount: 0,
      hasMore: false
    })
  },

  startRealtime: async () => {
    const user = useAuthStore.getState().user
    const existingChannel = get().channel

    if (!user || existingChannel) {
      return
    }

    const token = await getAuthToken()
    if (token) {
      supabase.realtime.setAuth(token)
    }

    const channel = supabase
      .channel(`notifications-realtime-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          get().refresh().catch(() => {
            // Errors are already recorded in store state; no-op here
          })
        }
      )
      .subscribe()

    set({ channel })
  },

  stopRealtime: () => {
    const channel = get().channel
    if (channel) {
      supabase.removeChannel(channel)
    }
    set({ channel: null })
  },

  reset: () => {
    get().stopRealtime()
    set({
      notifications: [],
      unreadCount: 0,
      hasMore: false,
      loading: false,
      error: null,
      isInitialized: false,
      fetchParams: { limit: DEFAULT_LIMIT }
    })
  }
}))

useAuthStore.subscribe(state => {
  const notificationStore = useNotificationStore.getState()
  const user = state.user

  if (!user) {
    previousUserId = null
    notificationStore.reset()
    return
  }

  if (previousUserId !== user.id) {
    notificationStore.reset()
    notificationStore.fetchNotifications().catch(() => {
      // Errors are stored within the notification store; no-op.
    })
  }

  previousUserId = user.id
})

